import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_HEATMAP } from './mockDataEjemplos';

const NOMBRES_DIA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DIAS_ORDEN = [1, 2, 3, 4, 5, 6, 0]; // Lunes a Domingo
const ESCALA = ['#E8EEFB', '#C3D6FE', '#8FB4FF', '#4C86F2', '#1E63E9'];

function colorDeValor(valor, max) {
    if (!valor || !max) return ESCALA[0];
    const paso = Math.min(4, Math.floor((valor / max) * ESCALA.length));
    return ESCALA[paso];
}

/**
 * Mapa de calor día × hora — grid de divs (no hace falta Chart.js). Responde "qué
 * día y a qué hora hace falta más gente" de un vistazo.
 * @param {{data: Array<{diaSemana: number, horario: string, total: number}>}} props
 */
const HeatmapDemanda = ({ data, compacto = false }) => {
    const { horas, celdas, max } = useMemo(() => {
        const porHora = new Map(); // "dia-hora" -> total
        const horasSet = new Set();
        (data || []).forEach((c) => {
            const hora = c.horario.split(':')[0];
            horasSet.add(hora);
            const clave = `${c.diaSemana}-${hora}`;
            porHora.set(clave, (porHora.get(clave) || 0) + c.total);
        });
        const maxVal = Math.max(0, ...porHora.values());
        return { horas: Array.from(horasSet).sort(), celdas: porHora, max: maxVal };
    }, [data]);

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Demanda por día y hora"
                        queMuestra="Te dice qué combinación de día de la semana y horario concentra más pedidos de turno, cruzando las dos variables a la vez en vez de mirarlas por separado."
                        ejemploVisual={<HeatmapDemanda compacto data={MOCK_HEATMAP} />}
                        comoLeerlo={[
                            'Cada celda cruza un día (columna) con una hora (fila); el número es la cantidad de turnos pedidos en esa combinación.',
                            'El color va de celeste muy claro (poca demanda) a azul oscuro (mucha demanda) — es una escala continua, no un semáforo de alarma.',
                            'La referencia de escala junto al título indica ese sentido claro→oscuro; la franja de abajo del todo muestra los mismos colores con el 0 y el máximo real de los datos en los extremos.',
                        ]}
                        analisis="El jueves a las 9 (10 turnos) es la celda más oscura del ejemplo, seguida de miércoles a las 10 (9) — son los dos momentos donde más conviene tener gente disponible. Los lunes y viernes, en cambio, están parejos y bajos durante toda la mañana: si hay que mover personal de un día a otro, esos dos días tienen más margen para ceder horas sin que se note."
                    />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '14px', paddingRight: '28px' }}>
                        <Box>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Demanda por día y hora</Typography>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto }}>
                                Qué día y a qué hora hace falta más gente.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, color: COLOR.grisTexto }}>Menor demanda</Typography>
                            {ESCALA.map((c) => <Box key={c} sx={{ width: 16, height: 10, background: c, borderRadius: '2px' }} />)}
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, color: COLOR.grisTexto }}>Mayor demanda</Typography>
                        </Box>
                    </Box>
                </>
            )}
            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: `56px repeat(${DIAS_ORDEN.length}, minmax(0, 1fr))`, gap: '3px', minWidth: 420 }}>
                    <Box />
                    {DIAS_ORDEN.map((d) => (
                        <Box key={d} sx={{ textAlign: 'center', fontFamily: FUENTE, fontSize: 11.5, fontWeight: 700, color: COLOR.grisTexto, paddingBottom: '4px' }}>
                            {NOMBRES_DIA[d]}
                        </Box>
                    ))}
                    {horas.map((hora) => (
                        <React.Fragment key={hora}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto, textAlign: 'right', paddingRight: '8px', alignSelf: 'center' }}>
                                {hora}:00
                            </Typography>
                            {DIAS_ORDEN.map((d) => {
                                const valor = celdas.get(`${d}-${hora}`) || 0;
                                const color = colorDeValor(valor, max);
                                const oscuro = valor / (max || 1) > 0.55;
                                return (
                                    <Box key={d} sx={{
                                        height: compacto ? 26 : 40, background: color, borderRadius: '4px', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, fontWeight: 600, color: oscuro ? '#fff' : COLOR.negro }}>
                                            {valor || ''}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </Box>
            </Box>
            {!compacto && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px' }}>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>0</Typography>
                    {ESCALA.map((c) => <Box key={c} sx={{ width: 20, height: 10, background: c }} />)}
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>{max}</Typography>
                </Box>
            )}
        </Card>
    );
};

export default HeatmapDemanda;
